import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../shadcn/ui/dialog";
import { Button } from "../shadcn/ui/button";
import { Mail, Copy, Check, ShieldAlert } from "lucide-react";
import { getOrgContactEmail, getOrgName } from "../utils/orgAdminAccess";

const ContactAdminModal = ({ isOpen, onClose, user, packageName }) => {
  const [copied, setCopied] = useState(false);

  const contactEmail = getOrgContactEmail(user);
  const orgName = getOrgName(user);

  const subject = encodeURIComponent(
    `Subscription Upgrade Request${packageName ? ` - ${packageName}` : ""}`
  );
  const body = encodeURIComponent(
    `Hi Admin,\n\nI would like to request an upgrade for our organization (${orgName}) on SPACIA${
      packageName ? ` to the "${packageName}" plan` : ""
    } so our team can access needed features.\n\nThank you!`
  );

  const mailtoLink = contactEmail
    ? `mailto:${contactEmail}?subject=${subject}&body=${body}`
    : null;

  const handleCopy = () => {
    if (!contactEmail) return;
    navigator.clipboard.writeText(contactEmail);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md p-6 bg-white rounded-2xl shadow-xl">
        <DialogHeader className="text-center sm:text-center items-center">
          <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 border border-blue-100 flex items-center justify-center mb-3">
            <ShieldAlert className="w-6 h-6" />
          </div>
          <DialogTitle className="text-xl font-bold text-gray-900">
            Organization Admin Required
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-600 mt-2 text-center">
            Only organization administrators can purchase or upgrade subscription plans for{" "}
            <span className="font-semibold text-gray-800">{orgName}</span>.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 space-y-4">
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
            <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-1">
              Organization Admin Contact
            </p>
            {contactEmail ? (
              <div className="flex items-center justify-between gap-2 mt-1">
                <span className="text-sm font-medium text-gray-900 truncate">
                  {contactEmail}
                </span>
                <button
                  type="button"
                  onClick={handleCopy}
                  className="p-1.5 text-gray-500 hover:text-gray-800 hover:bg-gray-200 rounded-md transition-colors"
                  title="Copy email address"
                >
                  {copied ? (
                    <Check className="w-4 h-4 text-green-600" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </button>
              </div>
            ) : (
              <p className="text-sm text-gray-600 italic">
                Please reach out to your team manager or internal administrator.
              </p>
            )}
          </div>

          <div className="flex flex-col gap-2 pt-2">
            {mailtoLink ? (
              <Button
                asChild
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5"
              >
                <a href={mailtoLink}>
                  <Mail className="w-4 h-4 mr-2" />
                  Email Administrator
                </a>
              </Button>
            ) : null}
            <Button
              variant="outline"
              onClick={onClose}
              className="w-full border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ContactAdminModal;
